import { useCallback, useContext, useState } from 'react';
import { Button } from 'reactstrap';
import { AlertCircle, RotateCcw, Search } from 'react-feather';
import { toast } from 'react-toastify';
import {
  DEBOUNCE_MS,
  EMPRESAS,
  SEDE_TODAS,
  TIPO_TODOS,
  TIPOS,
  empresaPorDefecto,
  mensajeErrorGuardado,
  useActualizarPrecio,
  useDebouncedValue,
  usePrecios,
  usePreciosCentros,
} from '../../features/precios';
import { AuthContext } from '../../context/authContext';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import TablaPrecios from '../../components/precios/TablaPrecios';
import ModalEditarPrecio from '../../components/precios/ModalEditarPrecio';
import Loader from '../../layouts/loader/Loader';
import colors from '../../theme/colors';

const OPCIONES_EMPRESA = EMPRESAS.map((e) => ({ value: e, label: e }));

const avisoStyle = {
  marginTop: '16px',
  padding: '32px',
  textAlign: 'center',
  border: `1px solid ${colors.border}`,
  borderRadius: '8px',
  background: colors.bgCard,
  fontSize: '0.875rem',
};

const claveFila = (fila) => (fila ? `${fila.idcentro}|${fila.codigocurso}` : 'sin-fila');

const Precios = () => {
  const { userOU } = useContext(AuthContext);
  const [empresa, setEmpresa] = useState(() => empresaPorDefecto(userOU));
  const [idcentro, setIdcentro] = useState(SEDE_TODAS);
  const [tipo, setTipo] = useState(TIPO_TODOS);
  const [busqueda, setBusqueda] = useState('');
  const search = useDebouncedValue(busqueda.trim(), DEBOUNCE_MS);

  const [fila, setFila] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState('');

  const centrosQuery = usePreciosCentros(empresa);
  const centros = centrosQuery.data ?? [];
  const {
    data: precios = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = usePrecios({
    headquarter: empresa,
    idcentro: idcentro === SEDE_TODAS ? '' : idcentro,
    tipo: tipo === TIPO_TODOS ? '' : tipo,
    search,
  });
  const actualizar = useActualizarPrecio(empresa);

  const opcionesSede = [
    { value: SEDE_TODAS, label: 'Todas' },
    ...centros.map((c) => ({ value: String(c.id), label: c.nombre })),
  ];

  const cambiarEmpresa = (e) => {
    setEmpresa(e.target.value);
    // Los ids de centro son de cada BD: el de la otra empresa no vale.
    setIdcentro(SEDE_TODAS);
  };

  const editar = useCallback((seleccionada) => {
    setErrorGuardado('');
    setFila(seleccionada);
    setModalAbierto(true);
  }, []);

  const guardar = (cambios) => {
    setErrorGuardado('');
    actualizar.mutate(
      { idcentro: fila.idcentro, codigocurso: fila.codigocurso, cambios },
      {
        onSuccess: () => {
          toast.success(`Precio de ${fila.codigocurso} actualizado`);
          setModalAbierto(false);
        },
        onError: (error) => {
          setErrorGuardado(mensajeErrorGuardado(error));
          if (error?.response?.status === 404) refetch();
        },
      },
    );
  };

  const renderResultados = () => {
    if (isLoading) {
      return (
        <div style={{ marginTop: '16px' }}>
          <Loader />
        </div>
      );
    }
    if (isError) {
      return (
        <div role="alert" style={{ ...avisoStyle, color: colors.danger }}>
          <div
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}
          >
            <AlertCircle size={16} aria-hidden="true" />
            <span>No se pudieron cargar los precios.</span>
          </div>
          <div>
            <Button color="light" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RotateCcw size={14} aria-hidden="true" /> Reintentar
            </Button>
          </div>
        </div>
      );
    }
    if (precios.length === 0) {
      return (
        <div style={{ ...avisoStyle, color: colors.textSecondary }}>
          No hay precios que coincidan con los filtros.
        </div>
      );
    }
    return (
      <div style={{ marginTop: '16px', opacity: isFetching ? 0.6 : 1 }} aria-busy={isFetching}>
        <TablaPrecios rows={precios} onEditar={editar} />
      </div>
    );
  };

  return (
    // .contentArea es un hijo flex sin min-width: 0: sin esta rejilla la tabla
    // ancha estiraria la pagina en vez de hacer scroll horizontal dentro de ui/Table.
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text, margin: 0 }}>
          Precios
        </h1>
        <p
          style={{
            color: colors.textSecondary,
            marginTop: '2px',
            marginBottom: 0,
            fontSize: '0.8rem',
          }}
        >
          Importes de actividades y ciclos por sede.
        </p>
      </div>

      <div
        role="search"
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
          padding: '16px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
        }}
      >
        <Select
          id="precios-empresa"
          label="Empresa"
          value={empresa}
          onChange={cambiarEmpresa}
          options={OPCIONES_EMPRESA}
        />
        <Select
          id="precios-sede"
          label="Sede"
          value={idcentro}
          onChange={(e) => setIdcentro(e.target.value)}
          options={opcionesSede}
          disabled={centrosQuery.isLoading}
          error={centrosQuery.isError ? 'No se pudieron cargar las sedes.' : undefined}
        />
        <Select
          id="precios-tipo"
          label="Tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          options={TIPOS}
        />
        <Input
          id="precios-buscar"
          label="Buscar curso"
          type="search"
          icon={Search}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Código o nombre"
          maxLength={100}
          autoComplete="off"
        />
      </div>

      {!isLoading && !isError && precios.length > 0 && (
        <div
          aria-live="polite"
          style={{ marginTop: '12px', fontSize: '0.8rem', color: colors.textSecondary }}
        >
          {precios.length} {precios.length === 1 ? 'precio' : 'precios'}
        </div>
      )}

      {renderResultados()}

      <ModalEditarPrecio
        key={claveFila(fila)}
        isOpen={modalAbierto}
        fila={fila}
        guardando={actualizar.isPending}
        errorGuardado={errorGuardado}
        onGuardar={guardar}
        onCerrar={() => setModalAbierto(false)}
        onClosed={() => setFila(null)}
      />
    </div>
  );
};

export default Precios;
